<template>
  <FormKit type="form" ref="formRef" :actions="false" #default="{ state: { valid } }" @submit="onSubmit" :sections-schema="{ messages: '' }">
    <div class="flex flex-col gap-4" data-testid="viewer-merge-video-modal-form">
      <div class="pt-4">
        <FormKit
          id="video-merge-input"
          data-testid="viewer-merge-video-input"
          type="text"
          label="Select a video to add"
          placeholder="Zight video URL address"
          :delay="200"
          :validation-rules="{ zightLink }"
          validation="required|zightLink:video"
          validation-visibility="dirty"
          v-model="url"
        >
          <template #suffix="{ state }">
            <Icon name="general.spinner" size="base" v-if="state.validating" />
            <Button v-if="state.dirty" :submit="false" type="link" variant="red" @click="() => reset('video-merge-input')">Remove</Button>
          </template>
          <template #messages="{ messages, state, classes, id }">
            <ul v-if="state.dirty || Object.keys(messages).length > 0" :class="classes.messages">
              <li
                v-for="message in messages"
                :key="message.key"
                :id="id + '-' + message.key"
                :class="messageClasses(classes.message)"
                :data-message-type="message.type"
              >
                <Icon name="general.exclamationCircleSolid" size="xs" />
                <span v-if="message.key == 'rule_zightLink'">
                  The link doesn’t look right. Double-check that:
                  <ul class="list-disc pl-4">
                    <li>It’s a ‘Zight’ video link.</li>
                    <li>You have permission to access it.</li>
                  </ul>
                </span>
                <span v-else>{{ message.value }}</span>
              </li>

              <!-- VALID -->
              <li v-if="Object.keys(messages).length === 0" :class="messageClasses(classes.message)">
                <Icon name="general.checkedCircleSolid" size="xs" class="text-black dark:text-zinc-400" />
                <span class="text-black dark:text-zinc-400">Link to video is correct</span>
              </li>
            </ul>
          </template>
        </FormKit>
      </div>

      <FormKit
        type="radio"
        label="Position"
        :options="{ start: 'Add at the beginning', end: 'Add at the end' }"
        v-model="position"
        @input="(e: 'start' | 'end') => videoMergeStore.setPosition(e)"
      />
      <Button
        type="solid"
        data-testid="viewer-merge-video-submit"
        :icon="isMerging ? 'general.spinner' : 'general.tick'"
        :disabled="!valid || isMerging"
        @click="formRef && formRef.node.submit()"
        >Apply</Button
      >
    </div>
  </FormKit>
</template>

<script setup lang="ts">
import { ref, Ref, onMounted } from 'vue';
import { reset, FormKitNode } from '@formkit/core';
import { storeToRefs } from 'pinia';

import { Button, Icon } from '@/storybook';
import { useVideoMergeStore } from '@/store/useVideoMergeStore';
import { useFormKit } from '@/composables/useFormKit';
import { useVideoMergeModal } from '@/composables/useVideoMergeModal';
import { zightLink } from '@/validators/ZightLink.validator';
import { useTrack, TrackEvents } from '@/composables/useTrack';

const { trackItemOwner } = useTrack();
const { isValidRef } = useFormKit();
const videoMergeStore = useVideoMergeStore();
const { closeVideoMergeModal } = useVideoMergeModal();

const url = ref('');
const formRef: Ref<{ node: FormKitNode } | null> = ref(null);
const position = ref(videoMergeStore.position);
const { isMerging } = storeToRefs(videoMergeStore);
const messageClasses = (classes: string) => classes + ' flex gap-2 pt-2';

const onSubmit = () => {
  if (!isValidRef(formRef)) {
    return;
  }

  trackItemOwner(TrackEvents.MERGE_VIDEO_APPLY, {
    position: position.value,
  });
  videoMergeStore.merge(url.value).then(() => {
    closeVideoMergeModal();
  });
};

onMounted(() => {
  trackItemOwner(TrackEvents.MERGE_VIDEO_MODAL_OPEN);
  const input = document.getElementById('video-merge-input') as HTMLTextAreaElement | null;
  if (input) {
    input.focus();
  }
});
</script>
